import { useCallback, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useGetAuthoringComponentQuery, useGetManifestQuery } from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { payloadId, useIntent, shown } from '../../api/useIntent'
import { fieldTarget, nodeAt } from '../../model/command'
import {
  composeDocument,
  regionGroup,
  type CompositionNode,
  type CompositionRegion,
  type RegionRef,
} from '../../model/composition'
import { allLinkIds, mintLinkId, decodeLinkPath, encodeLinkPath, samePath, withinPath, type LinkPath } from '../../model/link'
import { containable, intentArranging, structuralPayload, typeTitle } from '../../model/manifest'
import { pageFields } from '../../model/page'
import { previewOf } from '../../model/preview'
import { canonicalHost, flattenRoutes, menusInUse, resolveRouting, websiteFields } from '../../model/website'
import { Loading, QueryError } from '../../ui/QueryState'
import { useReorder } from '../../ui/reorder'
import { ContentPreview } from '../../ui/ContentPreview'
import { SitePreview } from '../../ui/SitePreview'
import type { Arranging } from '../page/Composition'
import { AddBlockDialog } from './AddBlockDialog'
import { BlockEditor } from './BlockEditor'
import { Blocks } from './Blocks'
import { PageOutline } from './PageOutline'
import { PageSettings, type PageSettingsValues } from './PageSettings'
import { PageHeader } from '../page/PageHeader'

/**
 * A page, as its owner meets it: what it says, and what it looks like.
 *
 * **Two documents, one screen.** A page's title and search description
 * are the Page's; its address, its menu name, its menu description and
 * whether it is the home page are
 * the Website's, because a slug belongs to the route that reaches a page
 * rather than to the page. That split is right in the model and invisible
 * to somebody who thinks of all six as "this page", so the settings form
 * shows them together and sends each field's intent to whichever
 * component owns it. The Structure face keeps them apart, where the
 * distinction is the point.
 *
 * **Everything the model says about a placement is left out** — link
 * ids, pin state, revision counts, structural intent names, the
 * link-or-inline question. Not because it is untrue, but because none of
 * it is a decision this reader is making. It is all one tab away on
 * Structure, over the same document and the same intents.
 *
 * **A page has four faces, and the blocks are the one it opens on.**
 * Settings — the title, the descriptions, the address, the menu name, the
 * home page — are facts an owner sets when a page is made and rarely
 * touches again, and they used to sit under the tree where every session
 * scrolled past them to reach the thing it came for. They are their own
 * address now, so the tree gets the screen. The fourth is Structure —
 * the same page with link ids, pin state and the node panel — which was
 * a separate mode with a separate address until the mode went. This
 * screen and `PageEditor` draw the identical tab strip, because they are
 * faces of one page rather than two renderings of it.
 *
 * **The editor opens in the tree, and the frame has a face of its own.**
 * The second column used to hold whichever of the editor and the preview
 * the reader had last caused — selecting a block took the page away at
 * the moment an author most wants to see it. Editing in place fixed
 * that; giving the frame the whole screen fixes the rest of it. A
 * preview sharing the window renders a customer's site at the site's own
 * *phone* breakpoints, so a column split down the middle was answering
 * "does my homepage look right" with a truthful picture of a page the
 * author was not asking about. One tab away and full width, it is the
 * page they meant.
 *
 * Editing keeps the width too, which the words need more than the tree
 * does: an inline editor is a rich-text control, and it used to be drawn
 * into half a screen indented by however deep its block sat.
 *
 * The faces are one selection between them, and it is `?sel=` — the same
 * link path this client has addressed a placement by since the
 * beginning, carried across every tab so a reader who checks the frame
 * comes back to the block they had open. Nothing about the arrangement
 * is state: which face is showing is the path, and what is open is the
 * query.
 */
export function ContentPage({ face = 'content' }: { face?: 'content' | 'preview' | 'settings' }) {
  const { tenant = '', site = '', pageId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const selection = decodeLinkPath(params.get('sel'))

  const manifest = useGetManifestQuery({ tenant })
  const page = useGetAuthoringComponentQuery({ tenant, id: pageId })
  const website = useGetAuthoringComponentQuery({ tenant, id: site })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)

  const [adding, setAdding] = useState<{ node: CompositionNode; region: CompositionRegion } | null>(null)

  const select = useCallback(
    (path: LinkPath) => {
      const next = new URLSearchParams(params)
      if (path.length === 0) next.delete('sel')
      else next.set('sel', encodeLinkPath(path))
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const root = useMemo(
    () => (page.data ? composeDocument(manifest.data, page.data) : undefined),
    [manifest.data, page.data],
  )

  const intentsFor = useCallback(
    (node: CompositionNode, region: CompositionRegion) => ({
      place: intentArranging(manifest.data, node.type, 'place', region.keyed),
      act: intentArranging(manifest.data, node.type, 'act', region.keyed),
      arrange: intentArranging(manifest.data, node.type, 'arrange', region.keyed),
      // A move always carries the region it is joining, so it is one of
      // the type's slot-carrying intents whichever region a drag began
      // in.
      move: intentArranging(manifest.data, node.type, 'move', true),
    }),
    [manifest.data],
  )

  const arrangingFor = useCallback(
    (node: CompositionNode, region: CompositionRegion): Arranging => {
      const intents = intentsFor(node, region)
      return {
        place: intents.place && { label: intents.place.title },
        act: intents.act && { label: intents.act.title },
        arrange: intents.arrange && { label: intents.arrange.title },
        move: region.keyed && intents.move ? { label: intents.move.title } : undefined,
      }
    },
    [intentsFor],
  )

  // Where a command about a node's own arrangement goes. An inline node
  // has no stream, so it is the page carrying the path.
  const holderOf = useCallback(
    (node: CompositionNode) => (page.data ? fieldTarget(page.data, node.path) : undefined),
    [page.data],
  )

  const place = useCallback(
    async (node: CompositionNode, region: CompositionRegion, child: Record<string, unknown>) => {
      const intent = intentsFor(node, region).place
      const holder = holderOf(node)
      if (!intent || !holder || holder.crossedLink) return
      await send(
        holder.id,
        intent.name,
        structuralPayload(intent, { child, slot: region.slot, position: region.linkIds.length }),
        holder.path,
      )
      setAdding(null)
    },
    [holderOf, intentsFor, send],
  )

  const reorder = useCallback(
    async (node: CompositionNode, region: CompositionRegion, order: string[]) => {
      const intent = intentsFor(node, region).arrange
      const holder = holderOf(node)
      if (!intent || !holder || holder.crossedLink) return
      await send(holder.id, intent.name, structuralPayload(intent, { slot: region.slot, order }), holder.path)
    },
    [holderOf, intentsFor, send],
  )

  const remove = useCallback(
    async (node: CompositionNode, region: CompositionRegion, placement: CompositionNode) => {
      const intent = intentsFor(node, region).act
      const holder = holderOf(node)
      if (!intent || !holder || holder.crossedLink || !placement.linkId) return
      await send(
        holder.id,
        intent.name,
        structuralPayload(intent, { slot: region.slot, link: placement.linkId }),
        holder.path,
      )
      if (withinPath(selection, placement.path)) select([])
    },
    [holderOf, intentsFor, select, selection, send],
  )

  // Carrying a block from one part of the page to another — the header
  // into the body — which a reorder cannot express: it names one region
  // and refuses a link id that is not already in it. The move intent
  // carries both ends and the fold does them in one revision, so the
  // block is never off the page and its content is never restated by
  // this client.
  const moveBetween = useCallback(
    async (from: RegionRef, to: RegionRef, linkId: string, position: number) => {
      const intent = intentsFor(to.node, to.region).move
      const holder = holderOf(to.node)
      if (!intent || !holder || holder.crossedLink) return
      await send(
        holder.id,
        intent.name,
        structuralPayload(intent, { link: linkId, fromSlot: from.region.slot, slot: to.region.slot, position }),
        holder.path,
      )
    },
    [holderOf, intentsFor, send],
  )

  // One hook for the page, because a drop that crosses two regions can
  // only be seen by something watching both.
  const dragging = useReorder<RegionRef>(
    (_group, order, meta) => {
      if (meta) reorder(meta.node, meta.region, order).catch(shown)
    },
    !sending,
    {
      // Only within one node, and only where its type declares a move.
      // There is no command that carries a block from a page's body into
      // a section's list, so the drop is refused rather than half done.
      allow: (from, to) =>
        encodeLinkPath(from.meta.node.path) === encodeLinkPath(to.meta.node.path) &&
        from.meta.region.keyed &&
        to.meta.region.keyed &&
        Boolean(intentsFor(to.meta.node, to.meta.region).move),
      send: (move) => moveBetween(move.from.meta, move.to.meta, move.id, move.position).catch(shown),
    },
  )

  // Where an intent editing the selected block's own fields goes: the
  // page for an inline block, the block's own component once the path
  // crosses a link.
  const target = useMemo(
    () => (page.data ? fieldTarget(page.data, selection) : undefined),
    [page.data, selection],
  )
  const linked = useGetAuthoringComponentQuery(
    { tenant, id: target?.id ?? '' },
    { skip: !target?.crossedLink || !target.id },
  )

  if (manifest.isLoading || page.isLoading) return <Loading label="Opening the page…" />
  if (page.error) return <QueryError error={page.error} title="Could not open this page" />
  if (!page.data || !root) return null

  // Named because the editor's own renderer is a closure, and a property
  // access does not stay narrowed across one.
  const pageDocument = page.data

  const fields = pageFields(page.data)
  const siteRoutes = website.data ? flattenRoutes(resolveRouting(website.data)) : []
  const route = siteRoutes.find((entry) => entry.targetId === pageId)
  const parent = route?.under ? siteRoutes.find((entry) => entry.route.page === route.under) : undefined
  const host = website.data ? canonicalHost(websiteFields(website.data)) : undefined

  const selectedNode = nodeAt(page.data, selection, (id) => (linked.data?.id === id ? linked.data : undefined))

  // The selected block's editor, drawn by the tree under the row it
  // belongs to. Every question it needs answered — where the intents go,
  // and whether the document behind a linked block has arrived — is
  // answered here, because this screen is where the selection already
  // resolves. The tree asks once per row and gets nothing for the rest.
  const renderEditor = (placement: CompositionNode) => {
    if (!samePath(selection, placement.path) || !target) return null

    // A linked block's content is another document, and until it lands
    // there is nothing to draw. Saying so under the row is the answer;
    // the row above it is still the thing the reader clicked.
    if (target.crossedLink && !linked.data) {
      return (
        <Box sx={{ px: 2, py: 2, borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
          {linked.error ? (
            <QueryError error={linked.error} title="Could not open this block" />
          ) : (
            <Loading label="Opening this block…" />
          )}
        </Box>
      )
    }

    if (!selectedNode) return null

    return (
      <BlockEditor
        tenant={tenant}
        manifest={manifest.data}
        node={selectedNode}
        target={target}
        document={target.crossedLink ? linked.data : pageDocument}
        busy={sending}
        send={(intent, payload) => send(target.id, intent, payload, target.path)}
        onClose={() => select([])}
      />
    )
  }

  const saveSettings = async (next: PageSettingsValues, was: PageSettingsValues) => {
    // One intent per fact that changed, each against the component that
    // owns it. Sent in sequence rather than in parallel so a refusal
    // stops the rest, and so the document the next one is folded onto is
    // the one the last produced.
    //
    // **The four intents are named here**, which this client does
    // everywhere else only where a derivation cannot choose. It cannot
    // here, and for the reason the site editor gives at length: a
    // payload says what an intent needs, not which arrangement it fills,
    // and a Website's `route_updated` and `home_set` are the same shape
    // — both name one existing route — so asking for "the act intent"
    // would get whichever came first. The form's controls are still
    // drawn only where the manifest says each one is declared.
    if (next.title !== was.title) {
      await send(pageId, 'page.retitled', { title: next.title })
    }
    // One intent for both search fields, because `page.seo_set` carries
    // the whole `seo` object and two sends would be the second
    // overwriting the first's half of it. The spread keeps whatever the
    // type holds that this form does not draw.
    if (next.seoDescription !== was.seoDescription || next.seoKeywords !== was.seoKeywords) {
      await send(pageId, 'page.seo_set', {
        seo: { ...(fields.seo ?? {}), description: next.seoDescription, keywords: next.seoKeywords },
      })
    }
    if (
      route &&
      (next.slug !== was.slug ||
        next.menuName !== was.menuName ||
        next.description !== was.description ||
        next.menus.join('\u0000') !== was.menus.join('\u0000'))
    ) {
      await send(site, 'website.route_updated', {
        page: route.route.page,
        slug: next.slug,
        title: next.menuName,
        description: next.description,
        menus: next.menus,
      })
    }
    if (route && next.isHome && !was.isHome) {
      await send(site, 'website.home_set', { page: route.route.page })
    }
  }

  const addChild = (
    typeName: string,
    id?: string,
    fields?: Record<string, unknown>,
    references?: Array<{ edgeId: string; name: string; id: string }>,
  ) => {
    if (!adding || !page.data) return
    const taken = new Set(allLinkIds(page.data.children))
    const child = id
      ? // A linked child names a target and states no type: the surface
        // reads the type off the target and refuses a stated one.
        { linkId: mintLinkId(id, taken), id: payloadId(tenant, id) }
      : // An inline child states its own type, because it is being
        // defined by this request and there is nothing to read it from.
        // The fields are the ones the type says a document of it is never
        // without, collected by the dialog: this placement is the node's
        // first revision, and one that could not be empty was refused.
        { linkId: mintLinkId(typeName.toLowerCase(), taken), type: typeName, fields: fields ?? {}, references }
    place(adding.node, adding.region, child).catch(shown)
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        tenant={tenant}
        site={site}
        pageId={pageId}
        page={page.data}
        website={website.data}
        manifest={manifest.data}
        face={face}
        params={params}
        urlPath={route?.urlPath}
        sending={sending}
      />

      {sendError && <Alert severity="error">{problemMessage(sendError, 'That change could not be made.')}</Alert>}

      {/* One face at a time, each in one column, and each in the width
          the shell gives it. The settings form used to be capped at
          720px; it was the only card on the site that was, and a card
          narrower than the ones beside it read as a mistake. */}
      {face === 'preview' ? (
        // **Two answers, in the order they are asked.** "What does this
        // page say now" is the question an author has while editing, and
        // only the first of these can answer it — the frame below serves
        // the current publication, so an edit made a minute ago is
        // deliberately absent from it. "What does it look like" is the
        // question they have before publishing, and only the frame can
        // answer that, because it is the site's own front-end rendering
        // the site's own page.
        //
        // Neither is a worse version of the other, which is why both are
        // here and both say which they are.
        <Stack spacing={2}>
          {page.data && <ContentPreview tenant={tenant} manifest={manifest.data} document={page.data} />}
          <SitePreview tenant={tenant} site={site} host={host} path={route?.urlPath} size="screen" />
        </Stack>
      ) : face === 'settings' ? (
        <Paper sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Typography variant="h3">Page settings</Typography>
            {/* Half of this form belongs to the site, so it waits for
                the site. Drawing it early would show an author "this
                page is not on the menu at all" about a page that is,
                which is the one wrong thing this panel can say. */}
            {website.isLoading ? (
              <Loading label="Reading the site…" />
            ) : website.error ? (
              <QueryError error={website.error} title="Could not read this page's address" />
            ) : (
              <PageSettings
                page={page.data}
                siteType={website.data?.type ?? ''}
                route={route}
                parent={parent}
                menuOptions={menusInUse(website.data ? websiteFields(website.data).routing?.nodes : undefined)}
                manifest={manifest.data}
                busy={sending}
                onSave={(next, was) => saveSettings(next, was).catch(shown)}
              />
            )}
          </Stack>
        </Paper>
      ) : (
        <Stack spacing={2}>
        <Paper>
          <Typography variant="h3" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            On this page
          </Typography>
          {root.regions.map((region) => (
            <Box key={region.slot ?? '(unnamed)'}>
              {root.regions.length > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', px: 2, pt: 1.5, textTransform: 'capitalize' }}
                >
                  {region.slot}
                </Typography>
              )}
              <Blocks
                node={root}
                region={region}
                tenant={tenant}
                manifest={manifest.data}
                arrangingFor={arrangingFor}
                titleOf={(typeName) => typeTitle(manifest.data, typeName)}
                previewOf={(node) => previewOf(manifest.data, node.type, node.fields)}
                dragging={dragging}
                groupOf={regionGroup}
                selection={selection}
                onSelect={select}
                onAdd={(node, target) => setAdding({ node, region: target })}
                onReorder={(node, region, order) => reorder(node, region, order).catch(shown)}
                onRemove={(node, region, placement) => remove(node, region, placement).catch(shown)}
                renderEditor={renderEditor}
                busy={sending}
              />
            </Box>
          ))}
        </Paper>

        {/* Below the blocks, because it is a reading *of* them: an
            author looks at what is on the page first, and at whether
            its structure holds together second. */}
        <PageOutline page={page.data} manifest={manifest.data} />
        </Stack>
      )}

      <AddBlockDialog
        tenant={tenant}
        open={adding !== null}
        where={adding?.region.slot ?? 'this page'}
        palette={adding ? containable(manifest.data, adding.node.type) : []}
        manifest={manifest.data}
        onClose={() => setAdding(null)}
        onLink={(typeName, id) => addChild(typeName, id)}
        onInline={(typeName, fields, references) => addChild(typeName, undefined, fields, references)}
        busy={sending}
      />
    </Stack>
  )
}

